import { useState, useRef } from 'react';
import { IconClear } from '@components/icons';
import styles from './Input.module.scss';

export type InputSize = 'regular' | 'slim';

export interface InputProps {
  /** Input value */
  value?: string;

  /** Placeholder text */
  placeholder?: string;

  /** Optional leading icon */
  leadingIcon?: React.ReactNode;

  /** Show clearable button */
  clearable?: boolean;

  /** Disabled state */
  disabled?: boolean;

  /** Input type */
  type?: 'text' | 'email' | 'password' | 'number';

  /** Size variant - 'regular' (52px) or 'slim' (34px) */
  size?: InputSize;

  /** Change handler */
  onChange?: (value: string) => void;

  /** Focus handler */
  onFocus?: () => void;

  /** Blur handler */
  onBlur?: () => void;

  /** Clear handler */
  onClear?: () => void;

  /** Key down handler */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;

  /** Error message — shows red border and message below the input */
  error?: string;

  /** Additional CSS classes */
  className?: string;

  /** Input name attribute */
  name?: string;

  /** Input id attribute */
  id?: string;
}

export const Input = ({
  value = '',
  placeholder = '',
  leadingIcon,
  clearable = false,
  disabled = false,
  type = 'text',
  size = 'regular',
  error,
  onChange,
  onFocus,
  onBlur,
  onClear,
  onKeyDown,
  className = '',
  name,
  id,
}: InputProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value);
  };

  const handleFocus = () => {
    setIsFocused(true);
    onFocus?.();
  };

  const handleBlur = () => {
    setIsFocused(false);
    onBlur?.();
  };

  const handleClear = () => {
    onChange?.('');
    onClear?.();
    inputRef.current?.focus();
  };

  const containerClassName = [
    styles.container,
    size === 'slim' && styles.slim,
    isFocused && styles.focused,
    isHovered && !isFocused && styles.hovered,
    disabled && styles.disabled,
    error && styles.error,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const showClearIcon = clearable && value && (isFocused || isHovered) && !disabled;

  return (
    <>
      <div
        className={containerClassName}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {leadingIcon && <span className={styles.leadingIcon}>{leadingIcon}</span>}

        <input
          ref={inputRef}
          type={type}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={onKeyDown}
          className={styles.field}
          aria-invalid={!!error}
          name={name}
          id={id}
        />

        {showClearIcon && (
          <button
            type="button"
            className={styles.clearButton}
            onClick={handleClear}
            aria-label="Clear input"
            tabIndex={-1}
          >
            <IconClear size={size === 'slim' ? 14 : 18} />
          </button>
        )}
      </div>
      {error && <p className={styles.errorMessage}>{error}</p>}
    </>
  );
};
